/**
 * 아이소메트릭 좌표 변환 (라니지1 2:1 다이아몬드 타일)
 *
 * 원본 라니지1은 정사각 타일을 45도 회전시킨 마름모(다이아몬드) 타일을 쓴다.
 * 타일 하나의 화면 크기는 가로:세로 = 2:1 이며, 타일의 위/아래 꼭짓점이
 * 화면 수직 방향을 향한다.
 *
 *            (tx, ty)
 *               /\        <- 위 꼭짓점
 *              /  \
 *             /    \      TILE_WIDTH  = 48 (가로 대각선)
 *             \    /      TILE_HEIGHT = 24 (세로 대각선)
 *              \  /
 *               \/        <- 아래 꼭짓점
 *
 * 서버(L1J)의 좌표는 정수 타일 단위 절대 좌표(최대 65535)다. 화면 픽셀 좌표와는
 * 전혀 다른 공간이므로, 렌더링할 때만 이 모듈로 변환하고 서버로 보낼 때는
 * 항상 타일 좌표를 그대로 쓴다.
 *
 * 변환식 (타일 -> 화면):
 *   screenX = (tx - ty) * (TILE_WIDTH  / 2)
 *   screenY = (tx + ty) * (TILE_HEIGHT / 2)
 *
 * tx가 늘면 화면 오른쪽 아래로, ty가 늘면 화면 왼쪽 아래로 이동한다.
 * 따라서 서버 기준 북(heading 0, ty-1)은 화면상 오른쪽 위가 된다.
 */

/** 타일의 가로 대각선 길이 (픽셀) */
export const TILE_WIDTH = 48;
/** 타일의 세로 대각선 길이 (픽셀). 2:1 비율 */
export const TILE_HEIGHT = 24;

export const HALF_TILE_WIDTH = TILE_WIDTH / 2;
export const HALF_TILE_HEIGHT = TILE_HEIGHT / 2;

export interface TilePoint {
  tx: number;
  ty: number;
}

export interface WorldPoint {
  x: number;
  y: number;
}

/**
 * 타일 좌표 -> Phaser 월드(화면) 좌표. 반환값은 타일의 중심점이다.
 */
export function tileToWorld(tx: number, ty: number): WorldPoint {
  return {
    x: (tx - ty) * HALF_TILE_WIDTH,
    y: (tx + ty) * HALF_TILE_HEIGHT,
  };
}

/**
 * Phaser 월드(화면) 좌표 -> 타일 좌표 (실수). 타일 경계 판정이 필요할 때 쓴다.
 */
export function worldToTileFloat(x: number, y: number): { tx: number; ty: number } {
  const a = x / HALF_TILE_WIDTH;
  const b = y / HALF_TILE_HEIGHT;
  return {
    tx: (b + a) / 2,
    ty: (b - a) / 2,
  };
}

/**
 * Phaser 월드(화면) 좌표 -> 가장 가까운 타일 좌표 (정수).
 */
export function worldToTile(x: number, y: number): TilePoint {
  const { tx, ty } = worldToTileFloat(x, y);
  return { tx: Math.round(tx), ty: Math.round(ty) };
}

/**
 * 아이소메트릭 렌더링의 그리기 순서.
 * (tx + ty)가 클수록 화면 아래쪽 = 카메라에 가까우므로 나중에 그려야 한다.
 */
export function tileDepth(tx: number, ty: number): number {
  return tx + ty;
}

/**
 * 방향 벡터 (heading 0-7). 서버 C_MoveChar.HEADING_TABLE_X/Y와 동일하다.
 * 0=북, 시계방향으로 증가한다.
 */
export const HEADING_VECTORS: ReadonlyArray<{ dx: number; dy: number }> = [
  { dx: 0, dy: -1 },  // 0 N
  { dx: 1, dy: -1 },  // 1 NE
  { dx: 1, dy: 0 },   // 2 E
  { dx: 1, dy: 1 },   // 3 SE
  { dx: 0, dy: 1 },   // 4 S
  { dx: -1, dy: 1 },  // 5 SW
  { dx: -1, dy: 0 },  // 6 W
  { dx: -1, dy: -1 }, // 7 NW
];

/**
 * 타일 공간의 이동 델타 -> heading(0-7).
 * 화면 각도가 아니라 타일 델타로 계산해야 서버와 방향이 일치한다.
 */
export function headingFromTileDelta(dx: number, dy: number): number {
  if (dx === 0 && dy === 0) return 0;

  // 축 성분이 지배적이면 정방향, 아니면 대각선으로 스냅한다.
  const absX = Math.abs(dx);
  const absY = Math.abs(dy);
  const sx = Math.sign(dx);
  const sy = Math.sign(dy);

  // 대각선으로 볼지(두 축의 크기가 비슷) 판단. 2배 이상 차이나면 축 방향으로 본다.
  const diagonal = absX > 0 && absY > 0 && absX * 2 > absY && absY * 2 > absX;

  let ux: number;
  let uy: number;
  if (diagonal) {
    ux = sx;
    uy = sy;
  } else if (absX >= absY) {
    ux = sx;
    uy = 0;
  } else {
    ux = 0;
    uy = sy;
  }

  const index = HEADING_VECTORS.findIndex((v) => v.dx === ux && v.dy === uy);
  return index >= 0 ? index : 0;
}

/**
 * 두 타일 사이의 거리(타일 단위). 서버 L1J는 맨해튼 거리로 화면 범위를 판정한다
 * (Point.isInScreen: |dx| + |dy| <= 19).
 */
export function tileDistance(ax: number, ay: number, bx: number, by: number): number {
  return Math.abs(ax - bx) + Math.abs(ay - by);
}

/**
 * 두 타일 사이의 **직선 거리**(타일 단위) — `max(|dx|, |dy|)`.
 *
 * 서버가 사거리를 판정할 때 쓰는 값과 같다
 * (`src/jp/l1j/server/types/Point.java`의 `getTileLineDistance`).
 *
 * 리니지는 8방향 게임이라 대각선으로 붙은 칸도 "한 칸 옆"이다. 여기에
 * 맨해튼 거리(tileDistance)를 쓰면 대각선 인접이 2로 계산되어, 근접 사거리
 * 1칸과 견주었을 때 **딱 붙어 있는 적을 멀다고 판정**한다. 그러면 그 자리에서
 * 치지 않고 적이 서 있는 칸으로 걸어가려 드는데, 그 칸은 적이 차지하고 있어
 * 경로가 없다 — 결국 붙어서도 영원히 공격하지 못한다.
 *
 * 그래서 **사거리 판정에는 반드시 이 함수를 쓴다.** 화면 범위 판정처럼 서버가
 * 맨해튼을 쓰는 곳은 tileDistance 그대로 둔다.
 */
export function tileLineDistance(ax: number, ay: number, bx: number, by: number): number {
  return Math.max(Math.abs(ax - bx), Math.abs(ay - by));
}

/**
 * 타일 하나의 마름모 꼭짓점을 화면 좌표로 반환한다 (위 -> 오른쪽 -> 아래 -> 왼쪽).
 * 타일 그리드/하이라이트를 그릴 때 쓴다.
 */
export function tileDiamondPoints(tx: number, ty: number): number[] {
  const { x, y } = tileToWorld(tx, ty);
  return [
    x, y - HALF_TILE_HEIGHT,
    x + HALF_TILE_WIDTH, y,
    x, y + HALF_TILE_HEIGHT,
    x - HALF_TILE_WIDTH, y,
  ];
}
